const valid = (point) => point && ['x', 'y', 'z'].every((axis) => Number.isFinite(point[axis]));

/** Two bounded draws reveal the actual source-part intake. Endpoints follow
 * the numeric supply surface and crane socket; particles never own contact or
 * damage. Reduced motion keeps a quiet connection and removes travelling motes. */
export function createSkyScrapMagnet(T, scene, own, reduced = () => false) {
  const segments = 48,
    strands = 3,
    moteCount = 24;
  const linePositions = new Float32Array(segments * strands * 2 * 3),
    motePositions = new Float32Array(moteCount * 3);
  const lineGeometry = own(new T.BufferGeometry()),
    moteGeometry = own(new T.BufferGeometry());
  lineGeometry.setAttribute('position', new T.BufferAttribute(linePositions, 3));
  moteGeometry.setAttribute('position', new T.BufferAttribute(motePositions, 3));
  const lineMaterial = own(
    new T.LineBasicMaterial({
      color: '#70cbd4',
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  const moteMaterial = own(
    new T.PointsMaterial({
      color: '#9be5e4',
      size: 0.14,
      transparent: true,
      opacity: 0.6,
      depthWrite: false,
      toneMapped: false,
      sizeAttenuation: true,
    }),
  );
  const lines = new T.LineSegments(lineGeometry, lineMaterial),
    motes = new T.Points(moteGeometry),
    direction = new T.Vector3(),
    side = new T.Vector3(),
    up = new T.Vector3();
  lines.name = 'Source salvage magnetic connection';
  motes.name = 'Source salvage travelling motes';
  lines.frustumCulled = false;
  motes.frustumCulled = false;
  lines.visible = false;
  motes.visible = false;
  scene.add(lines, motes);
  let endpoints = null;
  function strandPoint(from, to, t, phase, radius) {
    const envelope = Math.sin(Math.PI * t),
      angle = t * Math.PI * 8 + phase;
    return ['x', 'y', 'z'].map(
      (axis) =>
        from[axis] +
        (to[axis] - from[axis]) * t +
        envelope * radius * (side[axis] * Math.cos(angle) + up[axis] * Math.sin(angle)),
    );
  }
  return {
    draw(state, visible = true) {
      const boss = state.boss,
        s = visible && !boss?.defeated ? boss?.scrap : null;
      const from = s?.prop.authoredPose.sockets[s.selected + '_supply'],
        to = boss?.authoredPose?.sockets.scrap_crane;
      lines.visible = Boolean(s?.phase === 'intake' && valid(from) && valid(to));
      motes.visible = lines.visible && !reduced();
      endpoints = null;
      if (!lines.visible) return;
      direction.set(to.x - from.x, to.y - from.y, to.z - from.z);
      if (direction.length() < 0.05) {
        lines.visible = false;
        motes.visible = false;
        return;
      }
      direction.normalize();
      side.set(0, 1, 0).cross(direction);
      if (side.lengthSq() < 0.01) side.set(1, 0, 0).cross(direction);
      side.normalize();
      up.crossVectors(direction, side).normalize();
      const phase = reduced() ? 0 : s.clock * 0.7,
        radius = reduced() ? 0.12 : 0.36;
      for (let strand = 0; strand < strands; strand++)
        for (let segment = 0; segment < segments; segment++)
          for (let end = 0; end < 2; end++) {
            const point = strandPoint(
              from,
              to,
              (segment + end) / segments,
              phase + (strand * Math.PI * 2) / strands,
              radius,
            );
            linePositions.set(point, ((strand * segments + segment) * 2 + end) * 3);
          }
      for (let i = 0; i < moteCount; i++)
        motePositions.set(
          strandPoint(from, to, (i / moteCount + s.clock * 0.45) % 1, phase, 0.16),
          i * 3,
        );
      lineGeometry.attributes.position.needsUpdate = true;
      moteGeometry.attributes.position.needsUpdate = true;
      const fuel = s.selected === 'fuel';
      lineMaterial.color.set(fuel ? '#d49753' : '#70cbd4');
      moteMaterial.color.set(fuel ? '#efbe7a' : '#9be5e4');
      endpoints = { from: { ...from }, to: { ...to } };
    },
    diagnostics() {
      return {
        draws: Number(lines.visible) + Number(motes.visible),
        lineVertices: linePositions.length / 3,
        motes: motes.visible ? moteCount : 0,
        endpoints,
      };
    },
    dispose() {
      scene.remove(lines, motes);
    },
  };
}
